import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import rezort1 from '../images/rezort1.jpg';
import rezort2 from '../images/rezort2.jpg';
import LazyImage from '../components/LazyImage';

const Resort = () => {
  const { t, language } = useLanguage();

  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [imagesRef, imagesVisible] = useScrollAnimation();
  const [noteRef, noteVisible] = useScrollAnimation();
  const [featuresRef, featuresVisible] = useScrollAnimation();
  const [seasonsRef, seasonsVisible] = useScrollAnimation();

  return (
    <>
      <Helmet>
        <title>Rezort Litmanová – okolie chaty Barvinok</title>
        <meta
          name="description"
          content="Rezort Litmanová pri chate Barvinok – lyžovanie, turistika, cykloturistika a rodinné aktivity v krásnej prírode Pienin."
        />
      </Helmet>
    <div className="min-h-screen bg-white dark:bg-gray-950 pt-24">
      <section className="py-16 lg:py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header Section */}
          <div 
            ref={headerRef}
            className={`text-center mb-16 lg:mb-24 transition-all duration-800 ${headerVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
          >
            <span className="text-copper-400 text-sm font-semibold uppercase tracking-widest mb-4 block">
              {t('nav.resort')}
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              {t('resort.title')}
            </h2>
            <div className="w-24 h-1 bg-copper-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg">
              {t('resort.subtitle')}
            </p>
          </div>
        </div>

        {/* First Image Section - Text Left, Image Right */}
        <div 
          ref={imagesRef}
          className={`bg-gray-200 dark:bg-gray-800/50 py-16 lg:py-24 transition-all duration-800 delay-200 ${imagesVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Text Content */}
              <div className="order-2 lg:order-1 pl-6 lg:pl-8 xl:pl-12">
                <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                  {t('resort.title')}
                </h3>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  {t('resort.description')}
                </p>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                  {t('resort.description2')}
                </p>
                
                {/* Statistics */}
                {(() => {
                  const labels = {
                    sk: {
                      top1: 'Príjemný',
                      sub1: 'Pocit',
                      top2: 'TOP',
                      sub2: 'Prostredie',
                      top3: '20+',
                      sub3: 'Rokov',
                    },
                    pl: {
                      top1: 'Przyjemny',
                      sub1: 'Odczucie',
                      top2: 'TOP',
                      sub2: 'Otoczenie',
                      top3: '20+',
                      sub3: 'Lat',
                    },
                    en: {
                      top1: 'Pleasant',
                      sub1: 'Feeling',
                      top2: 'TOP',
                      sub2: 'Environment',
                      top3: '20+',
                      sub3: 'Years',
                    },
                  };

                  const current = labels[language] || labels.sk;

                  return (
                    <div className="grid grid-cols-3 gap-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-center">
                        <div className="text-3xl md:text-4xl font-bold text-copper-400 mb-1 md:mb-2">
                          {current.top1}
                        </div>
                        <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 tracking-wide">
                          {current.sub1}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl md:text-4xl font-bold text-copper-400 mb-1 md:mb-2">
                          {current.top2}
                        </div>
                        <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 tracking-wide">
                          {current.sub2}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl md:text-4xl font-bold text-copper-400 mb-1 md:mb-2">
                          {current.top3}
                        </div>
                        <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 tracking-wide">
                          {current.sub3}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Image - centered on all devices */}
              <div className="order-1 lg:order-2 relative group flex justify-center">
                <div className="absolute -inset-4 bg-gradient-to-r from-copper-500/20 to-copper-600/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                  <LazyImage 
                    src={rezort1} 
                    alt={t('resort.title')} 
                    className="w-full h-[250px] lg:h-[400px] xl:h-[450px] object-cover object-center transform group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Second Image Section - Image Left, Text Right */}
        <div 
          className={`bg-gray-100 dark:bg-gray-900 py-16 lg:py-24 transition-all duration-800 delay-300 ${imagesVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Image - centered */}
              <div className="order-1 relative group flex justify-center">
                <div className="absolute -inset-4 bg-gradient-to-r from-copper-500/20 to-copper-600/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                  <LazyImage 
                    src={rezort2} 
                    alt={t('resort.title')} 
                    className="w-full h-[250px] lg:h-[400px] xl:h-[450px] object-cover object-center transform group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>

              {/* Text Content */}
              <div className="order-2 pl-6 lg:pl-8 xl:pl-12">
                <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                  {t('resort.experienceTitle') || t('resort.title')}
                </h3>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  {t('resort.experienceDesc2') || t('resort.description2')}
                </p>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t('resort.experienceDesc') || t('resort.description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 bg-white dark:bg-gray-950 py-16 lg:py-24">

          {/* Important Note */}
          <div 
            ref={noteRef}
            className={`bg-gradient-to-r from-yellow-500/10 via-yellow-500/5 to-yellow-500/10 border-2 border-yellow-500/30 rounded-2xl mb-20 backdrop-blur-sm transition-all duration-800 delay-300 mt-12 lg:mt-16 ${noteVisible ? 'animate-scale-in opacity-100' : 'opacity-0'}`}
          >
            <div className="flex items-start space-x-4 p-8">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div>
                <h4 className="text-xl font-bold text-yellow-600 dark:text-yellow-400 mb-3">{t('resort.importantNote')}</h4>
                <p className="text-gray-700 dark:text-gray-200 leading-relaxed text-lg">
                  {t('resort.importantNoteText')}
                </p>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div 
            ref={featuresRef}
            className={`grid md:grid-cols-3 gap-8 mb-20 transition-all duration-800 delay-400 ${featuresVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
          >
            <div className="bg-gradient-to-br from-gray-50/90 to-gray-100/90 dark:from-gray-800/90 dark:to-gray-900/90 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-copper-500 transition-all hover:shadow-2xl hover:shadow-copper-500/20 hover:-translate-y-2 duration-300 backdrop-blur-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-copper-500/30 to-copper-600/20 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-copper-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('resort.location')}</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                {t('resort.locationDesc')}
              </p>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-copper-400 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">{t('resort.locationItem1')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-copper-400 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">{t('resort.locationItem2')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-gray-100/90 to-gray-200/90 dark:from-gray-800/90 dark:to-gray-900/90 p-8 rounded-2xl border border-gray-300 dark:border-gray-700 hover:border-copper-500 transition-all hover:shadow-2xl hover:shadow-copper-500/20 hover:-translate-y-2 duration-300 backdrop-blur-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-copper-500/30 to-copper-600/20 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-copper-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('resort.nature')}</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                {t('resort.natureDesc')}
              </p>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-copper-400 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">{t('resort.natureItem1')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-copper-400 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">{t('resort.natureItem2')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-gray-100/90 to-gray-200/90 dark:from-gray-800/90 dark:to-gray-900/90 p-8 rounded-2xl border border-gray-300 dark:border-gray-700 hover:border-copper-500 transition-all hover:shadow-2xl hover:shadow-copper-500/20 hover:-translate-y-2 duration-300 backdrop-blur-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-copper-500/30 to-copper-600/20 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-copper-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('resort.activities')}</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                {t('resort.activitiesDesc')}
              </p>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-copper-400 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">{t('resort.activitiesItem1')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-copper-400 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">{t('resort.activitiesItem2')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-copper-400 rounded-full"></div>
                  <span className="text-gray-700 dark:text-gray-300">{t('resort.activitiesItem3')}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Seasons and Facilities */}
          <div 
            ref={seasonsRef}
            className={`grid md:grid-cols-2 gap-8 mb-16 transition-all duration-800 delay-500 ${seasonsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}
          >
            <div className="bg-gradient-to-br from-gray-100/80 to-gray-200/80 dark:from-gray-800/80 dark:to-gray-900/80 backdrop-blur-sm p-8 rounded-2xl border border-gray-300 dark:border-gray-700 hover:border-copper-500 transition-all hover:shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('resort.seasons')}</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                {t('resort.seasonsDesc')}
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-3 bg-gray-200/50 dark:bg-gray-700/30 rounded-lg hover:bg-gray-300/50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="w-3 h-3 bg-copper-400 rounded-full"></div>
                  <span className="text-gray-800 dark:text-gray-200">{t('resort.season1')}</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-gray-200/50 dark:bg-gray-700/30 rounded-lg hover:bg-gray-300/50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="w-3 h-3 bg-copper-400 rounded-full"></div>
                  <span className="text-gray-800 dark:text-gray-200">{t('resort.season2')}</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-gray-200/50 dark:bg-gray-700/30 rounded-lg hover:bg-gray-300/50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="w-3 h-3 bg-copper-400 rounded-full"></div>
                  <span className="text-gray-800 dark:text-gray-200">{t('resort.season3')}</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-100/80 to-gray-200/80 dark:from-gray-800/80 dark:to-gray-900/80 backdrop-blur-sm p-8 rounded-2xl border border-gray-300 dark:border-gray-700 hover:border-copper-500 transition-all hover:shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('resort.facilities')}</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                {t('resort.facilitiesDesc')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center space-x-2 p-3 bg-gray-200/50 dark:bg-gray-700/30 rounded-lg hover:bg-gray-300/50 dark:hover:bg-gray-700/50 transition-colors">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-800 dark:text-gray-200 text-sm">{t('resort.facility1')}</span>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-gray-200/50 dark:bg-gray-700/30 rounded-lg hover:bg-gray-300/50 dark:hover:bg-gray-700/50 transition-colors">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-800 dark:text-gray-200 text-sm">{t('resort.facility2')}</span>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-gray-200/50 dark:bg-gray-700/30 rounded-lg hover:bg-gray-300/50 dark:hover:bg-gray-700/50 transition-colors">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-800 dark:text-gray-200 text-sm">{t('resort.facility3')}</span>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-gray-200/50 dark:bg-gray-700/30 rounded-lg hover:bg-gray-300/50 dark:hover:bg-gray-700/50 transition-colors">
                  <svg className="w-5 h-5 text-copper-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-800 dark:text-gray-200 text-sm">{t('resort.facility4')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Resort;

